import { defineStore } from 'pinia';
import { useUserStore } from './user';

// 默认配置常量
const DEFAULT_CONFIG = {
  // 通用配置
  layout: 'top', // 页面布局：'side'、'top'、'mix'、'drawer'
  isUseTabsRouter: false, // 是否启用标签路由
  s_tabs_header_mode: 'normal', // 标签路由样式
  showBreadcrumb: false, // 是否显示面包屑导航
  typeBreadcrumb: "/", // 面包屑间隔符
  showFooter: false, // 是否显示页尾
  brandTheme: 'tencent', // 主题色
  s_main_padding: '5px 10px', // 内边距
  s_main_background_color: "", // 主内容区背景颜色
  s_menu_color_lev1: "", // 导航条背景色
  s_menu_banner_h: 50, //logo高度
  s_menu_color_hover: "", // 菜单项 hover 时的背景颜色
  menuFontSize: 13, // 菜单字体大小
  scrollbarStyle: 'default', // 滚动条样式：default、thin、thick、rounded

  // 半通用设置(仅在个别布局下生效)
  s_menu_color_lev2: "", // 二级菜单颜色(top布局与side布局缩略模式下生效)
  s_menu_gap: 5, // 子项菜单间距(top布局与side布局缩略模式下生效)
  s_nav_height: 55, // 顶部导航栏高度（top/mix/drawer布局下生效）
  s_menu_height: 55, // 一级菜单按钮高度（top/mix布局下生效）
  s_menu_leftwidth: 220, // 左侧导航宽度（单位：px）（side/mix/drawer布局下生效）

  // 侧边栏布局独有配置
  s_side_menu_height: 45, // 侧边栏布局菜单高度
  s_side_menu_spacing: 0, // 侧边栏菜单间距

  // 抽屉布局独有配置
  s_drawer_menu_height: 45, // 抽屉布局菜单高度
  s_drawer_menu_spacing: 0, // 抽屉布局菜单间距

  // 混合布局独有配置
  isSidebarFixed: false, // 混合布局侧边菜单是否固定
  s_mix_top_menu_height: 55, // 混合布局顶部菜单高度
  s_mix_side_menu_height: 45, // 混合布局侧边菜单高度
  s_mix_top_menu_spacing: 0, // 混合布局顶部菜单间距
  s_mix_side_menu_spacing: 0, // 混合布局侧边菜单间距
  s_mix_top_menu_radius: 0, // 混合布局顶部菜单圆角

  // 顶部布局独有配置
  s_top_menu_radius: 0, // 顶部菜单圆角
  s_top_submenu_radius: 0, // 顶部子菜单圆角
  s_top_menu_spacing: 0, // 顶部菜单间距

  // 非配置修改部分
  s_menu_banner: "", // 菜单顶部横幅图
  s_menu_logo: "", // 菜单中的LOGO路径
  s_menu_fav: "", // 菜单中的favicon图标
  s_nav_img: "", // 导航背景图
  s_pages_array: [15, 30], // 分页配置
  s_pages_default: 15, // 默认分页数量
  s_main_background_img: "", // 主内容区背景图
  s_login_background: "", // 登录页背景图,在user.ts文件中被使用,本文件中未使用
  s_menu_background_color: "", // 未知背景色,未被使用,仅在获取时被获取到
  isConnectGlobalWs: false, // 是否连接全局 websocket
  s_sleep_time: 0, // 页面休眠超时（分钟），0 或未配置表示关闭；大于 0 时启用并按该值计时
  chartColors: {
    textColor: '#333333',
    placeholderColor: '#999999',
    borderColor: '#dddddd',
    containerColor: '#ffffff'
  }, // 图表颜色
};

declare module 'pinia' {
  export interface DefineStoreOptionsBase<S, Store> {
    persist?: boolean | {
      key?: string
      storage?: Storage
      paths?: string[]
    }
  }
}

export const useSettingStore = defineStore('setting', {
  state: () => ({
    isDark: false, // 控制黑暗模式
    displayMode: 'light', // 初始化时判断是什么模式
    ...DEFAULT_CONFIG
  }),
  actions: {
    _changeMenuColor() {
      const userStore = useUserStore();
      const url = userStore.serUrl;

      const prev = document.getElementById('dynamic-theme');

      if (prev && prev.parentNode) {
        prev.parentNode.removeChild(prev);
      }

      const themeMap = {
        tencent: '214',
        blue: '214',
        red: '355',
        orange: '27'
      }

      // 更新主题色
      document.documentElement.style.setProperty(
        '--D3-primary-h',
        themeMap[this.brandTheme] //要加颜色，加在上面定义的字典里，不然不准生效
      )

      if (
        !this.s_main_background_color
        && !this.s_menu_color_lev1
        && !this.s_menu_color_lev2
        && !this.s_menu_color_hover
        && !this.s_nav_img
        && !this.s_main_background_img
      ) {
        return;
      }

      let styleContent = `:root:not([theme-mode='dark']) {\n`;
      if (this.s_menu_color_lev1) {
        styleContent += `--d3-nav-bg-color: ${this.s_menu_color_lev1};\n`;
      }
      if (this.s_menu_color_lev2) {
        if (!this.s_nav_img) {
          styleContent += `--d3-nav-menu-color: ${this.s_menu_color_lev2};\n`;
        }
        //折叠菜单后的二级菜单
        styleContent += `--d3-nav-menu-ZD-color: ${this.s_menu_color_lev2};\n`;
      }
      if (this.s_menu_color_hover) {
        styleContent += `--d3-nav-hover-color: ${this.s_menu_color_hover};\n`;
      }
      if (this.s_main_background_color) {
        styleContent += `--d3-main-bg-color: ${this.s_main_background_color};\n`;
      }
      if (this.s_nav_img) {
        styleContent += `--d3-nav-bg-img:url('${url.trim()}${this.s_nav_img}');\n`;
      }
      if (this.s_main_background_img) {
        styleContent += `--d3-main-bg-img:url('${url.trim()}${this.s_main_background_img.trim()}');\n`;
      }
      styleContent += '}';

      const style = document.createElement('style');
      style.id = 'dynamic-theme';
      style.textContent = styleContent;
      document.head.appendChild(style);
    },


    // ——————暗黑模式切换——————
    toggleDark(v) {
      this.isDark = v;
      if (v) {
        document.documentElement.setAttribute('theme-mode', 'dark');
        this.displayMode = 'dark';
      } else {
        document.documentElement.setAttribute('theme-mode', '');
        this.displayMode = 'light';
      }
    },
    // ————————更新配置————————
    updateConfig(payload) {
      console.log('成功更新', JSON.parse(JSON.stringify(payload)));
      // 先恢复默认配置
      Object.assign(this, DEFAULT_CONFIG);

      // 再更新状态
      Object.keys(payload).forEach((key) => {
        this[key] = payload[key];
      });

      // this.cs()

      // 更新导航+菜单颜色
      this._changeMenuColor();
    },

    //测试区
    cs() {
      // side top mix drawer
      this.layout = 'top'
      // this.showBreadcrumb=true
      this.isUseTabsRouter = true
      this.brandTheme = 'red'

      // this.isSidebarCompact=false
      // this.s_menu_color_lev1 = undefined
      // this.s_menu_color_lev2 = undefined
      // this.s_menu_color_hover = undefined
      // this.s_main_background_img=undefined
      // this.s_main_background_color='red'
    }
  },
  // 持久化配置
  persist: true,
});
